// Paw & Dwell (2026-10-10): when a shopper picks an option value (for example a colour) and the
// current combination doesn't exist or is sold out, move the other options to the nearest
// available combination for that value. A size that isn't offered moves to the next size up
// (or the closest smaller one if there is none bigger); later options (e.g. Type) are kept
// whenever possible. Runs in the capture phase, before the theme's <variant-picker> handles the
// same change event, so the theme then fetches and renders the adjusted variant as usual.
// Variant data comes from the <script data-pd-variants> list rendered by blocks/variant-picker.liquid.
// 2026-10-10 (customer review): when an option is changed automatically, say so in the
// <p data-pd-notice> line under the picker (snippets/variant-main-picker.liquid), e.g.
// "Size changed to L — M isn't offered in Pink."

const WEIGHT_BASE = 1000;

/** @param {HTMLElement} picker */
function variantsFor(picker) {
  const id = picker.dataset.productId;
  if (!id) return null;
  const el = document.querySelector(`script[data-pd-variants="${CSS.escape(id)}"]`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || '[]');
  } catch {
    return null;
  }
}

/** @param {HTMLInputElement | HTMLSelectElement} el */
function positionOf(el) {
  const src = el instanceof HTMLSelectElement ? el.options[0] : el;
  const pos = Number.parseInt((src?.dataset.inputId || '').split('-')[0], 10);
  return Number.isNaN(pos) ? null : pos;
}

/** @param {HTMLElement} picker */
function controlsOf(picker) {
  /** @type {Map<number, {inputs: HTMLInputElement[], select: HTMLSelectElement | null}>} */
  const controls = new Map();
  for (const input of picker.querySelectorAll('fieldset input[type="radio"][data-input-id]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const pos = positionOf(input);
    if (pos === null) continue;
    if (!controls.has(pos)) controls.set(pos, { inputs: [], select: null });
    controls.get(pos)?.inputs.push(input);
  }
  for (const select of picker.querySelectorAll('select')) {
    const pos = positionOf(select);
    if (pos !== null) controls.set(pos, { inputs: [], select });
  }
  return controls;
}

function valuesOf(control) {
  return control.select ? Array.from(control.select.options, (o) => o.value) : control.inputs.map((i) => i.value);
}

function nameOf(control) {
  const el = control.select ? control.select.options[0] : control.inputs[0];
  return el?.dataset.optionName || 'Option';
}

/** @param {HTMLElement} picker @param {string} message */
function showNotice(picker, message) {
  const id = picker.dataset.productId;
  if (!id) return;
  const notice = document.querySelector(`[data-pd-notice="${CSS.escape(id)}"]`);
  if (!(notice instanceof HTMLElement)) return;
  notice.textContent = message;
  notice.hidden = !message;
}

function currentOf(control) {
  if (control.select) return control.select.value;
  return control.inputs.find((i) => i.checked)?.value ?? null;
}

/**
 * @param {HTMLElement & {updateSelectedOption: Function}} picker
 * @param {HTMLInputElement | HTMLSelectElement} target
 */
function adjust(picker, target) {
  const variants = variantsFor(picker);
  const changedPos = positionOf(target);
  if (!variants?.length || changedPos === null) return;

  const controls = controlsOf(picker);
  if (!controls.has(changedPos)) return;
  const current = new Map();
  for (const [pos, control] of controls) current.set(pos, currentOf(control));

  const matches = (v, pos) => v.options[pos - 1] === current.get(pos);
  const exact = variants.find((v) => [...controls.keys()].every((pos) => matches(v, pos)));
  showNotice(picker, '');
  if (exact?.available) return;

  const candidates = variants.filter((v) => v.available && matches(v, changedPos));
  if (!candidates.length) return;

  let best = null;
  let bestScore = Infinity;
  for (const v of candidates) {
    let score = 0;
    for (const [pos, control] of controls) {
      if (pos === changedPos) continue;
      const want = current.get(pos);
      const got = v.options[pos - 1];
      if (got === want) continue;
      const values = valuesOf(control);
      const wi = values.indexOf(want);
      const gi = values.indexOf(got);
      // Prefer the next value up (e.g. the next bigger size); a smaller one costs a little more.
      const distance = wi < 0 || gi < 0 ? values.length : gi > wi ? gi - wi : wi - gi + 0.5;
      score += distance * Math.pow(WEIGHT_BASE, pos - 1);
    }
    if (score < bestScore) {
      best = v;
      bestScore = score;
    }
  }
  if (!best) return;

  const changes = [];
  for (const [pos, control] of controls) {
    if (pos === changedPos) continue;
    const value = best.options[pos - 1];
    if (current.get(pos) === value) continue;
    changes.push({ name: nameOf(control), from: current.get(pos), to: value });
    if (control.select) {
      control.select.value = value;
      picker.updateSelectedOption(control.select);
    } else {
      const input = control.inputs.find((i) => i.value === value);
      if (input) picker.updateSelectedOption(input);
    }
  }

  // The theme reads the variant id for the URL from the changed option; point it at the new variant.
  const changedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
  if (changedOption) changedOption.dataset.variantId = String(best.id);

  if (changes.length) {
    const what = changes.map((c) => `${c.name} changed to ${c.to}`).join(', ');
    const was = changes.map((c) => c.from).filter(Boolean).join(' / ');
    const reason = exact ? 'is sold out in' : "isn't offered in";
    showNotice(picker, was ? `${what} — ${was} ${reason} ${current.get(changedPos)}.` : `${what}.`);
  }
}

if (!window.__pdAutoVariant) {
  window.__pdAutoVariant = true;
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      const picker = target.closest('variant-picker');
      if (!(picker instanceof HTMLElement) || typeof picker.updateSelectedOption !== 'function') return;
      const changedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
      if (changedOption?.dataset.connectedProductUrl) return; // combined listings: leave to the theme
      try {
        adjust(/** @type {any} */ (picker), target);
      } catch (error) {
        console.warn('[pd-auto-variant]', error);
      }
    },
    true
  );
}
